import { FC, Fragment } from 'react';
import DataRow, { DataRowItem } from '@/libs/antd/components/DataRow';
import { ArrowLeftOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import {
	CustomerBusinessDocument,
	CustomerLegalDocument,
	CustomerReferralDocument,
	MainInformation as MainInformationType,
	// ExistingFinancingLimitInformation,
	// FinancingApplicationLimitInformation,
} from '@/libs/fleet-api/services/types';
import { useModals } from './modals/ModalProvider';
import dayjs from 'dayjs';
import { SurveyInformation } from '@/libs/fleet-api/services/survey';
import { useApp } from '@/App';

type MainInformationProps = {
	mainInformation?: MainInformationType;
	customerLegalDocuments?: CustomerLegalDocument[];
	customerBusinessDocuments?: CustomerBusinessDocument[];
	customerReferralDocuments?: CustomerReferralDocument[];
	// prospectInformation: {
	// 	existingFinancingLimitInformation: ExistingFinancingLimitInformation[];
	// 	financingApplicationLimitInformation: FinancingApplicationLimitInformation[];
	// };
	surveyInformation?: SurveyInformation;
	isLoading?: boolean;
	extraItems?: DataRowItem[];
	showName?: boolean;
	onBack?: () => void;
};

const MainInformation: FC<MainInformationProps> = ({
	mainInformation = null,
	surveyInformation = null,
	isLoading = false,
	extraItems = [],
	showName = true,
	onBack,
}) => {
	const { navigate } = useApp();
	const {
		openCustomerDetail,
		openProspectDetail,
		openCustomerGroupDetail,
		openMapModal,
		openCustomerCheckingModal,
		openPraSurveyAnalysisModal,
		openPraSurveyDataModal,
	} = useModals();

	const customerInfoDataRow: DataRowItem[] = [
		[
			[
				'Nama Customer',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						openCustomerDetail(mainInformation!.customerId);
					}}
				>
					{mainInformation?.customerName}
				</a>,
			],
			[
				'Prospect ID',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						openProspectDetail(mainInformation!.prospectId);
					}}
				>
					{mainInformation?.prospectId}
				</a>,
			],
		],
		[
			['Kelas Industri', mainInformation?.industrySector?.industryClass],
			['Sumber Order', mainInformation?.orderSource],
		],
		[
			[
				'Portofolio Guideline',
				mainInformation?.industrySector?.portofolioGuideline,
			],
			[
				'Group Customer BMPP',
				mainInformation?.customerGroup?.id ? (
					<a
						href="#"
						className="trigger-popup"
						onClick={(e) => {
							e.preventDefault();
							openCustomerGroupDetail(mainInformation!.customerGroup?.id);
						}}
						aria-disabled={!mainInformation?.customerGroup?.value}
					>
						{mainInformation?.customerGroup?.value ?? '-'}
					</a>
				) : (
					'-'
				),
			],
		],
		[
			[
				'Dokumen',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						navigate('DMSDetailPage', {
							customerId: mainInformation!.customerId,
							param: {
								pagination: {
									pageNumber: 1,
									pageSize: 10,
								},
							},
						});
					}}
				>
					View Dokumen
				</a>,
			],
			[
				'Customer Checking',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						openCustomerCheckingModal(mainInformation?.prospectId ?? '');
					}}
				>
					Detail Analisa
				</a>,
			],
		],
		[
			[
				'Data Pra-Survey',
				<a
					href="#"
					onClick={(e) => {
						e.preventDefault();
						openPraSurveyDataModal(mainInformation?.prospectId ?? '');
					}}
					className="trigger-popup"
				>
					Detail Pra-Survey
				</a>,
			],
			[
				'Analisa Pra-Survey',
				<a
					href="#"
					onClick={(e) => {
						e.preventDefault();
						openPraSurveyAnalysisModal(mainInformation?.prospectId ?? '');
					}}
					className="trigger-popup"
				>
					Detail Analisa
				</a>,
			],
		],
		[
			[
				'SLIK Result',
				mainInformation?.slikResult
					? 'Kol ' + mainInformation?.slikResult
					: '-',
			],
		],
	];

	if (showName) {
		customerInfoDataRow.push([
			['FRM Name', mainInformation?.frm.value],
			['CA Name', mainInformation?.creditAnalyst.value],
		]);
	}

	if (surveyInformation) {
		customerInfoDataRow.push(
			[
				[
					'Lokasi Survey Kantor',
					<a
						href="#"
						className="trigger-popup"
						onClick={(e) => {
							e.preventDefault();
							openMapModal({
								title: 'Lokasi Survey Kantor',
								address: surveyInformation?.officeAddress,
								latitude: surveyInformation?.officeLat,
								longitude: surveyInformation?.officeLong,
								draggable: false,
								viewOnly: true,
							});
						}}
					>
						{surveyInformation?.officeAddress}
					</a>,
				],
				[
					'Tanggal Survey Kantor',
					dayjs(surveyInformation?.surveyOfficeDate).format('DD-MM-YYYY'),
				],
			],
			[
				[
					'Lokasi Survey Site',
					<a
						href="#"
						className="trigger-popup"
						onClick={(e) => {
							e.preventDefault();
							openMapModal({
								title: 'Lokasi Survey Site',
								address: surveyInformation?.siteAddress,
								latitude: surveyInformation?.siteLat,
								longitude: surveyInformation?.siteLong,
								draggable: false,
								viewOnly: true,
							});
						}}
					>
						{surveyInformation?.siteAddress}
					</a>,
				],
				[
					'Tanggal Survey Site',
					dayjs(surveyInformation?.surveySiteDate).format('DD-MM-YYYY'),
				],
			],
		);
	}

	if (extraItems.length) {
		customerInfoDataRow.push(...extraItems);
	}

	return (
		<Fragment>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 30,
				}}
			>
				<div>
					{typeof onBack === 'function' && (
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={onBack}
						>
							<ArrowLeftOutlined />
						</button>
					)}
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle>Main Information</FleetTitle>
				</div>
			</div>
			<DataRow
				items={customerInfoDataRow}
				loading={isLoading}
				style={{
					marginBottom: 20,
				}}
			/>
		</Fragment>
	);
};

export default MainInformation;
